// Pure DOM module – no fetch calls, no global state dependencies

const SlideRenderer = (() => {
    function escapeHtml(str) {
        if (typeof str !== 'string') return '';
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    const rtm = (str) => MathRender.renderTextWithMath(str);

    // Mirrors unsplashAttrs in utils/templates.js. The credit strip in app.js
    // finds photos by querying img[data-unsplash-credit] on the rendered DOM,
    // so a slide re-rendered through this module without these attributes loses
    // its photographer credit - which the Unsplash API terms require wherever a
    // photo is shown.
    function unsplashAttrs(credit) {
        if (!credit || !credit.name) return '';
        return ` data-unsplash-id="${escapeHtml(credit.id || '')}"`
            + ` data-unsplash-credit="${escapeHtml(credit.name)}"`
            + ` data-unsplash-profile="${escapeHtml(credit.profileUrl || '')}"`;
    }


    // Templates the teacher designed, keyed by "custom:<slug>". Populated once
    // after sign-in by setCustomTemplates so buildHtml can resolve them without
    // a server round-trip, the same as any built-in.
    let customTemplates = new Map();

    function setCustomTemplates(list) {
        customTemplates = new Map((list || []).map(t => [t.templateId, t]));
    }

    // Mirrors CALLOUTS in utils/templates.js. The concept slide's aside used to
    // be hard-coded to "Example" in both renderers; it now takes its framing
    // from callout_kind, and an unknown or missing kind falls back so that
    // decks written before the field existed render exactly as they did.
    const CALLOUTS = {
        'example': 'Example',
        'fun-fact': 'Fun fact',
        'remember': 'Remember',
        'try-this': 'Try this',
    };

    function calloutFor(kind) {
        const key = Object.prototype.hasOwnProperty.call(CALLOUTS, kind) ? kind : 'example';
        return { key: key, label: CALLOUTS[key] };
    }

    // Mirrors FALLBACK_ICON and iconBadge in utils/templates.js.
    const FALLBACK_ICON = 'star';

    function iconBadge(name) {
        return `<div class="slide__icon-badge">${SlideIcons.svg(name) || SlideIcons.svg(FALLBACK_ICON)}</div>`;
    }

    // Mirrors cardVisual in utils/templates.js.
    function cardVisual(item, imageClass, alt) {
        if (item.icon) return iconBadge(item.icon);
        if (item.image_url) {
            return `<img src="${escapeHtml(item.image_url)}" class="${imageClass}" alt="${escapeHtml(alt || '')}" loading="lazy"${unsplashAttrs(item.image_credit)}>`;
        }
        return '<div class="slide__image-placeholder"></div>';
    }

    function buildHtml(templateName, fields) {
        if (typeof templateName === 'string' && templateName.startsWith('custom:')) {
            const tmpl = customTemplates.get(templateName);
            if (!tmpl) return '<div class="slide slide--custom"></div>';
            return CustomTemplate.render(tmpl.compiled, fields, { renderTextWithMath: rtm, escapeHtml });
        }
        // Shared with utils/templates.js - see public/js/activityTemplates.js.
        // Guarded: a page that never loaded the module still renders every
        // other template.
        const activity = typeof ActivityTemplates !== 'undefined'
            ? ActivityTemplates.render(templateName, fields, { rtm, escapeHtml, cardVisual })
            : null;
        if (activity !== null) return activity;
        switch (templateName) {
            case 'title':
                return `<div class="slide slide--title">
  <div class="slide__title-panel">
    <div class="slide__heading">${rtm(fields.heading)}</div>
    <div class="slide__subtitle">${rtm(fields.subtitle)}</div>
  </div>
</div>`;
            case 'title-image': {
                const imgSrc = fields.image_url
                    ? `<img src="${escapeHtml(fields.image_url)}" class="slide__title-image-bg" alt="${escapeHtml(fields.image_keyword || '')}" loading="lazy"${unsplashAttrs(fields.image_credit)}>`
                    : '<div class="slide__title-image-placeholder"></div>';
                return `<div class="slide slide--title-image">
  ${imgSrc}
  <div class="slide__title-tint"></div>
  <div class="slide__title-image-content">
    <div class="slide__heading">${rtm(fields.heading)}</div>
    <div class="slide__subtitle">${rtm(fields.subtitle)}</div>
  </div>
</div>`;
            }

            case 'content': {
                const bullets = Array.isArray(fields.bullets) ? fields.bullets : [];
                const items = bullets.map(b => `    <li>${rtm(b)}</li>`).join('\n');
                return `<div class="slide slide--content">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <ul class="slide__bullets">
${items}
  </ul>
</div>`;
            }

            case 'two-column': {
                const lb = Array.isArray(fields.left_bullets) ? fields.left_bullets
                    : (fields.left_body ? [fields.left_body] : []);
                const rb = Array.isArray(fields.right_bullets) ? fields.right_bullets
                    : (fields.right_body ? [fields.right_body] : []);
                const leftItems  = lb.map(b => `      <li>${rtm(b)}</li>`).join('\n');
                const rightItems = rb.map(b => `      <li>${rtm(b)}</li>`).join('\n');
                return `<div class="slide slide--two-column">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__col">
    <div class="slide__col-heading">${rtm(fields.left_heading)}</div>
    <ul class="slide__col-bullets">
${leftItems}
    </ul>
  </div>
  <div class="slide__col">
    <div class="slide__col-heading">${rtm(fields.right_heading)}</div>
    <ul class="slide__col-bullets">
${rightItems}
    </ul>
  </div>
</div>`;
            }

            case 'comparison-diagram': {
                const lb = Array.isArray(fields.left_bullets) ? fields.left_bullets : [];
                const rb = Array.isArray(fields.right_bullets) ? fields.right_bullets : [];
                const leftItems  = lb.map(b => `      <li>${rtm(b)}</li>`).join('\n');
                const rightItems = rb.map(b => `      <li>${rtm(b)}</li>`).join('\n');
                const imgSrc = fields.diagram_url
                    ? `<img src="${escapeHtml(fields.diagram_url)}" class="slide__image" alt="${escapeHtml(fields.diagram_keyword || '')}" loading="lazy">`
                    : '<div class="slide__image-placeholder"></div>';
                return `<div class="slide slide--comparison-diagram">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__comparison-cols">
    <div class="slide__col">
      <div class="slide__col-heading">${rtm(fields.left_heading)}</div>
      <ul class="slide__col-bullets">
${leftItems}
      </ul>
    </div>
    <div class="slide__col">
      <div class="slide__col-heading">${rtm(fields.right_heading)}</div>
      <ul class="slide__col-bullets">
${rightItems}
      </ul>
    </div>
  </div>
  <div class="slide__image-wrap">
    ${imgSrc}
  </div>
</div>`;
            }

            case 'process-flow': {
                const steps = Array.isArray(fields.steps) ? fields.steps : [];
                const stepItems = steps.map((s, i) => {
                    const colonIdx = s.indexOf(': ');
                    let label = '', text = s;
                    if (colonIdx > 0) { label = s.slice(0, colonIdx); text = s.slice(colonIdx + 2); }
                    const arrow = i < steps.length - 1 ? '<div class="slide__flow-arrow">&rarr;</div>' : '';
                    return `    <div class="slide__flow-step">
      <div class="slide__flow-num">${i + 1}</div>
      ${label ? `<div class="slide__flow-label">${rtm(label)}</div>` : ''}
      <div class="slide__flow-text">${rtm(text)}</div>
    </div>
${arrow ? `    ${arrow}\n` : ''}`;
                }).join('');
                return `<div class="slide slide--process-flow">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__flow-row">
${stepItems}  </div>
</div>`;
            }

            // A row of ideas that have no photograph and no diagram - Drinking,
            // Washing, Growing, Trade, Safety. term-grid could carry icons, but it
            // is a VOCABULARY slide: its cards are terms and definitions, and the
            // planner treats it that way. This is the other thing.
            case 'icon-cards': {
                const items = Array.isArray(fields.items) ? fields.items : [];
                const cards = items.map(it => `    <div class="slide__icon-card">
      ${iconBadge(it.icon)}
      <div class="slide__icon-card-label">${rtm(it.label)}</div>
      <div class="slide__icon-card-text">${rtm(it.text)}</div>
    </div>`).join('\n');
                return `<div class="slide slide--icon-cards">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__icon-card-row">
${cards}
  </div>
</div>`;
            }

            // Openers and closers. Mirrors utils/templates.js.
            case 'hook': {
                const img = fields.image_url
                    ? `<img src="${escapeHtml(fields.image_url)}" class="slide__hook-image" alt="${escapeHtml(fields.image_keyword || '')}" loading="lazy"${unsplashAttrs(fields.image_credit)}>`
                    : '<div class="slide__image-placeholder"></div>';
                return `<div class="slide slide--hook">
  <div class="slide__hook-text">
    <div class="slide__heading">${rtm(fields.heading)}</div>
    <div class="slide__hook-question">${rtm(fields.question)}</div>
  </div>
  <div class="slide__hook-image-wrap">
    ${img}
  </div>
</div>`;
            }

            case 'recap': {
                const points = Array.isArray(fields.takeaways) ? fields.takeaways : [];
                const items = points.map(t => `      <li>${rtm(t)}</li>`).join('\n');
                const img = fields.image_url
                    ? `<img src="${escapeHtml(fields.image_url)}" class="slide__recap-image" alt="${escapeHtml(fields.image_keyword || '')}" loading="lazy"${unsplashAttrs(fields.image_credit)}>`
                    : '<div class="slide__image-placeholder"></div>';
                const callout = calloutFor(fields.callout_kind);
                const box = fields.callout_text
                    ? `    <div class="slide__example-box slide__example-box--${callout.key}">
      <div class="slide__example-label">${escapeHtml(callout.label)}</div>
      <div class="slide__example-text">${rtm(fields.callout_text)}</div>
    </div>`
                    : '';
                return `<div class="slide slide--recap">
  <div class="slide__recap-image-wrap">
    ${img}
  </div>
  <div class="slide__recap-text">
    <div class="slide__heading">${rtm(fields.heading)}</div>
    <div class="slide__recap-intro">${rtm(fields.intro)}</div>
    <ul class="slide__recap-list">
${items}
    </ul>
${box}
  </div>
</div>`;
            }

            case 'photo-process': {
                const steps = Array.isArray(fields.steps) ? fields.steps : [];
                const cols = steps.map((step, i) => {
                    const img = cardVisual(step, 'slide__pstep-image', step.label);
                    return `    <div class="slide__pstep">
      <div class="slide__pstep-image-wrap">${img}${i < steps.length - 1 ? '<div class="slide__pstep-arrow">&rarr;</div>' : ''}</div>
      <div class="slide__pstep-label">${rtm(step.label)}</div>
      <div class="slide__pstep-detail">${rtm(step.detail)}</div>
    </div>`;
                }).join('\n');
                return `<div class="slide slide--photo-process">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__pstep-row">
${cols}
  </div>
</div>`;
            }

            // Activities. Mirrors the same two cases in utils/templates.js -
            // this module is the client-side twin of that switch, and the
            // template picker previews through it, so a template missing here
            // previews as a blank slide however well it generates.
            case 'questions':
            case 'questions-answers': {
                const reveal = templateName === 'questions-answers';
                const items = Array.isArray(fields.items) ? fields.items : [];
                // The reveal keeps the QUESTION on screen and puts the answer under
                // it, rather than swapping one for the other. A class that can no
                // longer see what was asked cannot check its own work, and the
                // teacher has nothing left to talk around.
                const rows = items.map((it, i) => `      <li class="slide__q-item">
        <span class="slide__q-num">${i + 1}</span>
        <span class="slide__q-body">
          <span class="slide__q-text">${rtm(it.question)}</span>
          ${reveal ? `<span class="slide__q-answer" data-reveal="${i + 1}">${rtm(it.answer)}</span>` : ''}
        </span>
      </li>`).join('\n');
                return `<div class="slide slide--questions${reveal ? ' slide--questions-answers' : ''}">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__q-instruction">${rtm(reveal ? (fields.answer_instruction || 'Check your answers below') : fields.instruction)}</div>
  <ol class="slide__q-list">
${rows}
  </ol>
</div>`;
            }

            case 'ordering':
            case 'ordering-answers': {
                const reveal = templateName === 'ordering-answers';
                const steps = Array.isArray(fields.steps) ? fields.steps : [];
                // display_order is decided once, when the slide is filled, and
                // stored - so re-rendering an existing slide (an edit, a re-export,
                // the library thumbnail) never reshuffles it under the teacher.
                const order = Array.isArray(fields.display_order) && fields.display_order.length === steps.length
                    ? fields.display_order
                    : steps.map((_, i) => i);
                const shown = reveal ? steps.map((s, i) => ({ s, n: i + 1 })) : order.map(i => ({ s: steps[i], n: null }));
                // Same panel as a question row, so the two activities read as one
                // family. Unnumbered rows keep a dash in the number's place: the
                // slot has to be reserved or the text jumps sideways when the
                // teacher advances to the reveal.
                const cards = shown.map(({ s, n }) => `    <div class="slide__order-row">
      <span class="slide__order-num${n ? '' : ' slide__order-num--blank'}">${n || '&ndash;'}</span>
      <span class="slide__order-text">${rtm(s)}</span>
    </div>`).join('\n');
                return `<div class="slide slide--ordering${reveal ? ' slide--ordering-answers' : ''}">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__q-instruction">${rtm(fields.instruction)}</div>
  <div class="slide__order-list">
${cards}
  </div>
</div>`;
            }

            // Support ladder. Mirrors the same four cases in utils/templates.js;
            // data-reveal is explained there.
            case 'sentence-starters': {
                const starters = Array.isArray(fields.starters) ? fields.starters : [];
                const words = Array.isArray(fields.word_bank) ? fields.word_bank : [];
                const rows = starters.map(s => `      <li class="slide__starter">${rtm(s)}</li>`).join('\n');
                const chips = words.map(w => `        <span class="slide__word-chip">${rtm(w)}</span>`).join('\n');
                return `<div class="slide slide--task slide--sentence-starters">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__task-question">${rtm(fields.question)}</div>
  <div class="slide__task-cols">
    <ul class="slide__starter-list">
${rows}
    </ul>
    <div class="slide__word-bank">
      <div class="slide__task-label">Word bank</div>
      <div class="slide__word-chips">
${chips}
      </div>
    </div>
  </div>
</div>`;
            }

            case 'structured-prompt': {
                const steps = Array.isArray(fields.steps) ? fields.steps : [];
                const rows = steps.map((it, i) => `    <li class="slide__q-item">
      <span class="slide__q-num">${i + 1}</span>
      <span class="slide__q-body">
        <span class="slide__q-text">${rtm(it.prompt)}</span>
        ${it.starter ? `<span class="slide__step-note">${rtm(it.starter)}</span>` : ''}
      </span>
    </li>`).join('\n');
                return `<div class="slide slide--task slide--structured-prompt">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__task-question">${rtm(fields.question)}</div>
  <ol class="slide__q-list">
${rows}
  </ol>
</div>`;
            }

            case 'worked-example': {
                const steps = Array.isArray(fields.steps) ? fields.steps : [];
                const rows = steps.map((it, i) => `    <li class="slide__q-item" data-reveal="${i + 1}">
      <span class="slide__q-num">${i + 1}</span>
      <span class="slide__q-body">
        <span class="slide__q-text">${rtm(it.step)}</span>
        ${it.why ? `<span class="slide__step-note">${rtm(it.why)}</span>` : ''}
      </span>
    </li>`).join('\n');
                return `<div class="slide slide--task slide--worked-example">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__task-question">${rtm(fields.problem)}</div>
  <ol class="slide__q-list">
${rows}
  </ol>
  ${fields.answer ? `<div class="slide__worked-answer" data-reveal="${steps.length + 1}"><span class="slide__task-label">Answer</span>${rtm(fields.answer)}</div>` : ''}
</div>`;
            }

            case 'talk-task': {
                const beats = [['Think', fields.think], ['Pair', fields.pair], ['Share', fields.share]];
                const cards = beats.map(([label, text], i) => `    <div class="slide__talk-card">
      <span class="slide__q-num">${i + 1}</span>
      <div class="slide__task-label">${label}</div>
      <div class="slide__talk-text">${rtm(text)}</div>
    </div>`).join('\n');
                return `<div class="slide slide--task slide--talk-task">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__task-question">${rtm(fields.prompt)}</div>
  <div class="slide__talk-row">
${cards}
  </div>
</div>`;
            }

            case 'quote':
                return `<div class="slide slide--quote">
  <div class="slide__quote">&ldquo;${rtm(fields.quote_text)}&rdquo;</div>
  <div class="slide__attribution">&ndash; ${rtm(fields.attribution)}</div>
</div>`;

            case 'section':
                return `<div class="slide slide--section">
  <div class="slide__section-number">${escapeHtml(fields.section_number)}</div>
  <div class="slide__section-title">${rtm(fields.section_title)}</div>
  <div class="slide__section-subtitle">${rtm(fields.section_subtitle)}</div>
</div>`;

            case 'image-text': {
                // Two shapes for the same content. "bleed" (the default) runs the
                // photo to the slide edge; "circle" crops it to a disc inside a
                // ring. Alternating them, and the side they sit on, is most of what
                // stops a deck of picture-and-text slides looking like one slide
                // repeated.
                const circle = fields.layout === 'circle';
                const left = fields.image_side === 'left';
                const mods = `${circle ? ' slide--image-text--circle' : ''}${left ? ' slide--image-text--left' : ''}`;
                const imgSrc = fields.image_url
                    ? `<img src="${escapeHtml(fields.image_url)}" class="slide__image" alt="${escapeHtml(fields.image_keyword || '')}" loading="lazy"${unsplashAttrs(fields.image_credit)}>`
                    : '<div class="slide__image-placeholder"></div>';
                return `<div class="slide slide--image-text${mods}">
  <div class="slide__content">
    <div class="slide__heading">${rtm(fields.heading)}</div>
    <div class="slide__body">${rtm(fields.body)}</div>
  </div>
  <div class="slide__image-wrap">
    ${imgSrc}
  </div>
</div>`;
            }
            case 'timeline': {
                const events = Array.isArray(fields.events) ? fields.events : [];
                const eventItems = events.map(e => {
                    const colonIdx = e.indexOf(': ');
                    let label = '', text = e;
                    if (colonIdx > 0) { label = e.slice(0, colonIdx); text = e.slice(colonIdx + 2); }
                    return `    <div class="slide__event">
      <div class="slide__event-dot"></div>
      <div class="slide__event-content">
        ${label ? `<span class="slide__event-label">${rtm(label)}</span>` : ''}<span class="slide__event-text">${rtm(text)}</span>
      </div>
    </div>`;
                }).join('\n');
                return `<div class="slide slide--timeline">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__timeline-list">
    <div class="slide__timeline-line"></div>
${eventItems}
  </div>
</div>`;
            }

            case 'definition':
                return `<div class="slide slide--definition">
  <div class="slide__term-label">Definition</div>
  <div class="slide__term">${rtm(fields.term)}</div>
  <div class="slide__definition-box">
    <div class="slide__definition">${rtm(fields.definition)}</div>
  </div>
  ${fields.example ? `<div class="slide__example">${rtm(fields.example)}</div>` : ''}
</div>`;

            case 'term-grid': {
                const items = Array.isArray(fields.items) ? fields.items : [];
                const cols = items.map(item => {
                    const img = cardVisual(item, 'slide__term-image', item.term);
                    return `    <div class="slide__term-item">
      ${img}
      <div class="slide__term-name">${rtm(item.term)}</div>
      <div class="slide__term-definition">${rtm(item.definition)}</div>
    </div>`;
                }).join('\n');
                return `<div class="slide slide--term-grid">
  <div class="slide__heading">${rtm(fields.heading)}</div>
  <div class="slide__term-grid-row">
${cols}
  </div>
</div>`;
            }

            case 'concept': {
                const callout = calloutFor(fields.callout_kind);
                const img = fields.image_url
                    ? `<img src="${escapeHtml(fields.image_url)}" class="slide__image" alt="${escapeHtml(fields.heading || '')}" loading="lazy">`
                    : '<div class="slide__image-placeholder"></div>';
                return `<div class="slide slide--concept">
  <div class="slide__content">
    <div class="slide__badge">${escapeHtml(fields.category || '')}</div>
    <div class="slide__heading">${rtm(fields.heading)}</div>
    <div class="slide__body">${rtm(fields.body)}</div>
    <div class="slide__key-fact-box">
      <div class="slide__key-fact">${rtm(fields.key_fact)}</div>
    </div>
    <div class="slide__example-box slide__example-box--${callout.key}">
      <div class="slide__example-label">${escapeHtml(callout.label)}</div>
      <div class="slide__example-text">${rtm(fields.example_text)}</div>
    </div>
  </div>
  <div class="slide__image-wrap">
    ${img}
  </div>
</div>`;
            }

            default:
                return `<div class="slide slide--content">
  <div class="slide__heading">${rtm(fields.heading || '')}</div>
</div>`;
        }
    }

    // Auto-fit: shrink content that overflows the fixed 960×540 canvas.
    // CSS zoom scales the whole subtree (rem-sized text included), unlike a
    // font-size tweak. Applied to the slide itself and any height-constrained
    // content columns; iterates because shrinking changes line wrapping.
    // Keep in sync with AUTO_FIT_SCRIPT in utils/pdf.js.
    function fitBox(box) {
        for (let i = 0; i < 4; i++) {
            if (!box.clientHeight) return; // hidden – nothing to measure
            const overflow = box.scrollHeight - box.clientHeight;
            if (overflow <= 2) return;
            const prev = parseFloat(box.dataset.fitZoom || '1');
            const next = Math.max(0.6, prev * box.clientHeight / box.scrollHeight);
            if (next >= prev) return;
            box.dataset.fitZoom = String(next);
            for (const child of box.children) child.style.zoom = next;
        }
    }

    function autoFitSlides(container) {
        container.querySelectorAll('.slide').forEach(slide => {
            // zoom on an absolutely-positioned child scales its left/top as well
            // as its box, so a hand-placed layout MOVES rather than shrinking.
            // Teacher-made templates opt out wholesale and opt individual boxes
            // back in with data-slot-fit="shrink".
            if (slide.hasAttribute('data-no-autofit')) {
                slide.querySelectorAll('[data-slot-fit="shrink"]').forEach(fitBox);
                return;
            }
            fitBox(slide);
            slide.querySelectorAll('.slide__content').forEach(fitBox);
        });
    }

    function positionTimelineLines(container) {
        container.querySelectorAll('.slide--timeline .slide__timeline-list').forEach(list => {
            const line = list.querySelector('.slide__timeline-line');
            const dots = list.querySelectorAll('.slide__event-dot');
            if (!line || dots.length < 2) { if (line) line.style.display = 'none'; return; }
            const topY    = dots[0].closest('.slide__event').offsetTop + 11;
            const bottomY = dots[dots.length - 1].closest('.slide__event').offsetTop + 11;
            line.style.top    = topY + 'px';
            line.style.height = (bottomY - topY) + 'px';
        });
    }

    // Render a slide into container from template + fields
    function renderSlide(container, templateName, fields) {
        container.innerHTML = buildHtml(templateName, fields);
        positionTimelineLines(container);
        autoFitSlides(container);
    }

    // Render a slide into container from raw HTML (server-built or Sonnet-edited)
    function renderSlideHtml(container, html) {
        container.innerHTML = html;
        positionTimelineLines(container);
        MathRender.renderMathInElement(container);
        autoFitSlides(container);
    }

    // Stream HTML token-by-token into container (used during edit streaming)
    function updateSlideHtml(container, rawHtml) {
        container.innerHTML = rawHtml;
        positionTimelineLines(container);
        MathRender.renderMathInElement(container);
        autoFitSlides(container);
    }

    return { renderSlide, renderSlideHtml, updateSlideHtml, buildHtml, autoFitSlides, setCustomTemplates };
})();
